import { Navigate, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { buildPovPages } from "@/data/povPages";
import { buildPovPages2 } from "@/data/povPages2";
import { buildPovPages3 } from "@/data/povPages3";
import { buildPovPages4 } from "@/data/povPages4";

const SANS = "'Noto Sans', system-ui, sans-serif";
const SERIF = "'Noto Serif', Georgia, serif";
const pct = (v, total) => `${(v / total) * 100}%`;

const Positioned = ({ it, h }) => {
  const lh = it.lh || 1.2;
  return (
    <p
      style={{
        position: "absolute",
        left: pct(it.x, 960),
        top: pct(it.y - ((lh - 1) * it.size) / 2, h),
        width: it.w ? pct(it.w, 960) : undefined,
        whiteSpace: it.w ? undefined : "nowrap",
        fontSize: `${it.size / 9.6}cqw`,
        lineHeight: lh,
        color: it.color,
        fontWeight: it.bold ? 700 : 400,
        letterSpacing: it.ls,
        textTransform: it.upper ? "uppercase" : undefined,
        fontFamily: it.serif ? SERIF : SANS,
        margin: 0,
      }}
    >
      {it.text}
    </p>
  );
};

const PageDesktop = ({ page, index }) => (
  <section data-testid={`pov-page-${index + 1}`} className="pov-page" style={{ backgroundColor: page.bg || "#ffffff", aspectRatio: `960 / ${page.height}` }}>
    {page.image && (
      <>
        <img
          src={page.image.src}
          alt=""
          loading={index > 1 ? "lazy" : "eager"}
          style={{ position: "absolute", left: pct(page.image.x, 960), top: pct(page.image.y, page.height), width: pct(page.image.w, 960), height: pct(page.image.h, page.height), objectFit: "cover" }}
        />
        {page.image.overlay && (
          <span style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.88) 34%, rgba(10,10,10,0.45) 62%, rgba(10,10,10,0.12) 100%)" }} />
        )}
      </>
    )}
    {page.bar && <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pct(page.barW, 960), backgroundColor: page.bar }} />}
    {(page.rects || []).map((r, i) => (
      <span key={i} style={{ position: "absolute", left: pct(r.x, 960), top: pct(r.y, page.height), width: pct(r.w, 960), height: pct(r.h, page.height), backgroundColor: r.fill, borderRadius: r.round ? "50%" : undefined }} />
    ))}
    {page.items.filter((it) => it.text).map((it, i) => <Positioned key={i} it={it} h={page.height} />)}
  </section>
);

const mobileStyle = (it) => {
  if (it.serif && it.size >= 25) return "font-[700] text-[1.65rem] leading-[1.18]";
  if (it.serif) return "font-[700] text-[1.25rem] leading-[1.25]";
  if (it.size >= 30) return "font-[700] text-[1.75rem] leading-[1.18]";
  if (it.size >= 18) return "font-[700] text-[1.2rem] leading-[1.3]";
  if (it.upper) return "font-[700] text-[10.5px] uppercase tracking-[0.16em]";
  if (it.bold) return "font-[700] text-[15px] leading-[1.35]";
  if (it.size <= 7) return "text-[11px] leading-[1.5]";
  return "text-[13.5px] leading-[1.65]";
};

const PageMobile = ({ page }) => (
  <section className="px-6 py-10 sm:px-10 md:px-14" style={{ backgroundColor: page.bg || "#ffffff" }}>
    {page.image && <img src={page.image.src} alt="" loading="lazy" className="mb-8 aspect-[4/3] w-full object-cover" />}
    <div className="mx-auto w-full max-w-2xl space-y-4">
      {page.items
        .filter((it) => it.text && !it.desktopOnly)
        .map((it, i) => (
          <p
            key={i}
            className={mobileStyle(it)}
            style={{ color: it.color, fontFamily: it.serif ? SERIF : SANS }}
          >
            {it.mobileNum && <span style={{ color: "#8d1f35" }} className="mr-3 text-[11px] font-[700] tracking-[0.12em]">{it.mobileNum}</span>}
            {it.text}
          </p>
        ))}
    </div>
  </section>
);

export default function Insight() {
  const { slug } = useParams();
  const { content } = useLang();
  const insight = (content.insights || []).find((i) => i.slug === slug);
  if (!insight) return <Navigate to="/" replace />;

  const builders = {
    "ia-ne-remplace-pas-mecaniquement-l-humain": buildPovPages,
    "la-puissance-de-l-ia-depend-des-infrastructures": buildPovPages2,
    "a-l-ere-des-contenus-synthetiques-la-confiance-ne-peut-plus-etre-presumee": buildPovPages3,
    "quand-l-ia-agit-la-responsabilite-doit-etre-organisee": buildPovPages4,
  };
  const pages = builders[insight.slug](insight);

  return (
    <article data-testid="insight-page">
      <div className="hidden lg:block" data-testid="insight-document">
        {pages.map((p, i) => <PageDesktop key={i} page={p} index={i} />)}
      </div>
      <div className="lg:hidden">
        {pages.map((p, i) => <PageMobile key={i} page={p} />)}
      </div>
    </article>
  );
}
